import { useState, useRef, useCallback, useEffect } from 'react';
import { Button, Box, Typography, IconButton, Modal, Checkbox, FormControlLabel, Stack, CircularProgress } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import RefreshIcon from '@mui/icons-material/Refresh';
import { analyzeFoodImage } from '../services/openaiService';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';

const HELP_KEY = 'foodcamera_hide_help';

// Restaurar rutas originales de las imágenes
const ayudaImgOk = '/imgs_ayuda_img_ok.jpg';
const ayudaImgNo = '/imgs_ayuda_img_no.jpg';

// Detección de dispositivo móvil
const isMobile = () => {
  const userAgent = navigator.userAgent;
  const isMobileUA = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(userAgent);
  const isTouch = 'ontouchstart' in window;
  const isSmallScreen = window.innerWidth < 768;
  
  return isMobileUA || (isTouch && isSmallScreen);
};

// Verificación de contexto seguro
const isSecureContext = () => {
  return window.isSecureContext || location.protocol === 'https:' || location.hostname === 'localhost';
};

// Configuraciones de cámara optimizadas
const getCameraConfigurations = () => {
  const configs = [
    // Config 1: Ideal para móviles
    { 
      video: { 
        facingMode: isMobile() ? 'environment' : 'user',
        width: { ideal: 1280, max: 1920 },
        height: { ideal: 720, max: 1080 }
      }
    },
    // Config 2: Sin facingMode
    { 
      video: { 
        width: { ideal: 1280, max: 1920 },
        height: { ideal: 720, max: 1080 }
      }
    },
    // Config 3: Resolución media
    { 
      video: { 
        width: { ideal: 640 },
        height: { ideal: 480 }
      }
    },
    // Config 4: Básico
    { video: true }
  ];
  
  return configs;
};

export const FoodCamera: React.FC<{ onCancel?: () => void; onCapture?: (img: string) => void }> = ({ onCancel, onCapture }) => {
  const [hasPermission, setHasPermission] = useState<boolean | null>(null);
  const [image, setImage] = useState<string | null>(null);
  const [showHelp, setShowHelp] = useState(!localStorage.getItem(HELP_KEY));
  const [helpChecked, setHelpChecked] = useState(false);
  const [cameraActive, setCameraActive] = useState(false);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [analysisResult, setAnalysisResult] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [currentConfigIndex, setCurrentConfigIndex] = useState(0);
  const [videoReady, setVideoReady] = useState(false);
  const [videoLoaded, setVideoLoaded] = useState(false);
  const [debugInfo, setDebugInfo] = useState<string>('');
  const videoRef = useRef<HTMLVideoElement>(null);
  const streamRef = useRef<MediaStream | null>(null);

  // Limpiar al desmontar
  useEffect(() => {
    return () => {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
      }
    };
  }, []);

  // Detectar cuando el video se está reproduciendo
  useEffect(() => {
    const videoElement = videoRef.current;
    if (!videoElement || !cameraActive) return;

    const handlePlaying = () => {
      console.log('🎥 Video reproduciéndose - marcando como listo');
      setVideoReady(true);
    };

    const handleCanPlay = () => {
      console.log('📹 Video puede reproducirse');
      if (videoElement.readyState >= 3) {
        setVideoReady(true);
      }
    };

    videoElement.addEventListener('playing', handlePlaying);
    videoElement.addEventListener('canplay', handleCanPlay);

    return () => {
      videoElement.removeEventListener('playing', handlePlaying);
      videoElement.removeEventListener('canplay', handleCanPlay);
    };
  }, [cameraActive]);

  const tryGetUserMedia = async (config: MediaStreamConstraints, timeoutMs: number = 8000) => {
    const streamPromise = navigator.mediaDevices.getUserMedia(config);
    const timeoutPromise = new Promise<MediaStream>((_, reject) =>
      setTimeout(() => reject(new Error('Timeout al acceder a la cámara')), timeoutMs)
    );
    
    return Promise.race([streamPromise, timeoutPromise]);
  };

  const getErrorMessage = (error: any) => {
    if (error.name === 'NotAllowedError') {
      return 'Permisos denegados. Ve a configuración del navegador y permite la cámara.';
    } else if (error.name === 'NotFoundError') {
      return 'No se encontró cámara en el dispositivo.';
    } else if (error.name === 'NotReadableError') {
      return 'La cámara está siendo usada por otra app. Cierra otras aplicaciones.';
    } else if (error.name === 'OverconstrainedError') {
      return 'Configuración de cámara incompatible con el dispositivo.';
    } else if (error.message.includes('Timeout')) {
      return 'La cámara tardó demasiado en responder.';
    } else if (!isSecureContext()) {
      return 'Se requiere HTTPS para usar la cámara en móviles.';
    } else {
      return `Error de cámara: ${error.message || 'Error desconocido'}`;
    }
  };

  // Configuración del video optimizada
  const setupVideoElement = async (videoElement: HTMLVideoElement, stream: MediaStream) => {
    console.log('🎬 Configurando video element para stream:', stream);
    setVideoReady(false);
    setVideoLoaded(false);
    setDebugInfo('Configurando video...');
    
    try {
      // Configurar propiedades del video
      videoElement.muted = true;
      videoElement.defaultMuted = true;
      videoElement.autoplay = true;
      videoElement.controls = false;
      videoElement.loop = false;
      videoElement.preload = 'auto';
      videoElement.playsInline = true;
      videoElement.disablePictureInPicture = true;
      
      // Configurar atributos para móviles
      const attributes = {
        'autoplay': 'true',
        'muted': 'true',
        'playsinline': 'true',
        'webkit-playsinline': 'true',
        'controls': 'false',
        'preload': 'auto',
        'x-webkit-airplay': 'allow',
        'x5-video-player-type': 'h5',
        'x5-video-player-fullscreen': 'false',
        'x5-video-orientation': 'portraint',
        'disablepictureinpicture': 'true'
      };
      
      Object.entries(attributes).forEach(([attr, value]) => {
        videoElement.setAttribute(attr, value);
      });
      
      // Aplicar estilos
      const styles = {
        width: '100%',
        height: '100%',
        objectFit: 'cover',
        backgroundColor: '#000',
        display: 'block',
        visibility: 'visible',
        opacity: '1',
        position: 'relative',
        zIndex: '1',
        borderRadius: '16px',
        transform: isMobile() ? 'none' : 'scaleX(-1)'
      };
      
      Object.entries(styles).forEach(([prop, value]) => {
        (videoElement.style as any)[prop] = value;
      });
      
      setDebugInfo('Asignando stream...');
      
      // Asignar el stream
      videoElement.srcObject = stream;
      
      // Esperar metadatos
      await new Promise<void>((resolve, reject) => {
        const timeout = setTimeout(() => {
          console.log('⏰ Timeout metadatos - continuando...');
          resolve();
        }, 5000);
        
        const onLoadedMetadata = () => {
          clearTimeout(timeout);
          console.log(`✅ Metadatos: ${videoElement.videoWidth}x${videoElement.videoHeight}`);
          setVideoLoaded(true);
          resolve();
        };
        
        const onError = (e: Event) => {
          clearTimeout(timeout);
          console.error(`❌ Error metadatos: ${e.type}`);
          reject(new Error('Error al cargar metadatos'));
        };
        
        videoElement.addEventListener('loadedmetadata', onLoadedMetadata, { once: true });
        videoElement.addEventListener('error', onError, { once: true });
        
        if (videoElement.readyState >= 1) {
          clearTimeout(timeout);
          onLoadedMetadata();
        }
      });
      
      setDebugInfo('Iniciando reproducción...');
      
      // Métodos de reproducción
      const playMethods = [
        // Método 1: Play directo
        async () => {
          console.log('🎬 Método 1: Play directo');
          await videoElement.play();
        },
        
        // Método 2: Forzar muted y play
        async () => {
          console.log('🔇 Método 2: Forzar muted');
          videoElement.muted = true;
          videoElement.volume = 0;
          await videoElement.play();
        },
        
        // Método 3: Load y play
        async () => {
          console.log('🔄 Método 3: Load y play');
          videoElement.load();
          await new Promise(r => setTimeout(r, 200));
          await videoElement.play();
        },
        
        // Método 4: Resetear srcObject
        async () => {
          console.log('🔄 Método 4: Reset srcObject');
          const currentStream = videoElement.srcObject;
          videoElement.srcObject = null;
          await new Promise(r => setTimeout(r, 100));
          videoElement.srcObject = currentStream;
          await new Promise(r => setTimeout(r, 200));
          await videoElement.play();
        }
      ];
      
      // Probar métodos de reproducción
      let success = false;
      for (let i = 0; i < playMethods.length; i++) {
        try {
          await playMethods[i]();
          console.log(`✅ Método ${i + 1} exitoso`);
          success = true;
          break;
        } catch (error: any) {
          console.warn(`❌ Método ${i + 1} falló: ${error.message}`);
        }
      }
      
      if (success) {
        setVideoReady(true);
        setDebugInfo('Video activo');
        console.log('🎉 Video funcionando!');
      } else {
        console.warn('⚠️ Todos los métodos fallaron - configurando reproducción manual');
        setDebugInfo('Toca para activar');
        
        // Fallback: reproducción manual
        const setupManualPlay = () => {
          const manualPlayButton = document.createElement('button');
          manualPlayButton.innerText = 'TOCAR PARA ACTIVAR CÁMARA';
          manualPlayButton.style.cssText = `
            position: absolute;
            top: 50%;
            left: 50%;
            transform: translate(-50%, -50%);
            z-index: 1000;
            background: #FF4444;
            color: white;
            border: none;
            padding: 20px 40px;
            font-size: 18px;
            font-weight: bold;
            border-radius: 10px;
            cursor: pointer;
          `;
          
          manualPlayButton.onclick = async () => {
            try {
              await videoElement.play();
              console.log('✅ Reproducción manual exitosa!');
              setVideoReady(true);
              manualPlayButton.remove();
            } catch (e: any) {
              console.error(`❌ Reproducción manual falló: ${e.message}`);
            }
          };
          
          if (videoElement.parentElement) {
            videoElement.parentElement.appendChild(manualPlayButton);
          }
        };
        
        setupManualPlay();
        
        // Marcar como listo después de un tiempo
        setTimeout(() => {
          if (!videoReady) {
            console.log('⏰ Timeout - marcando como listo');
            setVideoReady(true);
          }
        }, 2000);
      }
      
    } catch (error: any) {
      console.error(`💥 Error en setupVideoElement: ${error.message}`);
      throw error;
    }
  };

  // useEffect para configurar video cuando esté disponible
  useEffect(() => {
    if (cameraActive && videoRef.current && streamRef.current) {
      console.log('🎯 Video element disponible - configurando...');
      setupVideoElement(videoRef.current, streamRef.current).catch(error => {
        console.error(`💥 Error configurando video: ${error.message}`);
      });
    }
  }, [cameraActive, videoRef.current]);

  const startCamera = useCallback(async () => {
    try {
      setIsLoading(true);
      setErrorMessage('');
      
      console.log('🚀 Iniciando cámara...');
      console.log(`📱 Móvil: ${isMobile()}, HTTPS: ${isSecureContext()}`);

      // Verificaciones básicas
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        throw new Error('Tu navegador no soporta acceso a la cámara');
      }

      if (!isSecureContext()) {
        throw new Error('Se requiere HTTPS para usar la cámara en móviles');
      }

      const configurations = getCameraConfigurations();
      let stream: MediaStream | null = null;
      let lastError: any = null;

      // Intentar todas las configuraciones
      for (let i = currentConfigIndex; i < configurations.length; i++) {
        try {
          console.log(`🔧 Config ${i + 1}/${configurations.length}`);
          stream = await tryGetUserMedia(configurations[i]);
          setCurrentConfigIndex(i);
          console.log(`✅ Config ${i + 1} exitosa!`);
          break;
        } catch (error) {
          console.warn(`❌ Config ${i + 1} falló`);
          lastError = error;
          continue;
        }
      }

      if (!stream) {
        throw lastError || new Error('No se pudo acceder a la cámara');
      }

      console.log('📹 Stream obtenido - guardando para configuración...');

      // Guardar el stream y activar cámara - el video se configurará en el useEffect
      streamRef.current = stream;
      setHasPermission(true);
      setCameraActive(true);
      console.log('🎉 Cámara iniciada exitosamente!');

    } catch (error: any) {
      console.error(`💥 Error al iniciar cámara: ${error.message}`);
      setHasPermission(false);
      setCameraActive(false);
      setErrorMessage(getErrorMessage(error));
    } finally {
      setIsLoading(false);
    }
  }, [currentConfigIndex]);

  const stopCamera = useCallback(() => {
    console.log('🛑 Deteniendo cámara...');
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => {
        track.stop();
        console.log(`Track ${track.kind} detenido`);
      });
      streamRef.current = null;
    }
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
    setCameraActive(false);
    setVideoReady(false);
    setVideoLoaded(false);
    setDebugInfo('');
  }, []);

  const captureImage = useCallback(() => {
    if (!videoRef.current) {
      console.error('No hay referencia al video para capturar');
      return;
    }

    try {
      const video = videoRef.current;
      
      if (video.videoWidth === 0 || video.videoHeight === 0) {
        throw new Error('El video no tiene dimensiones válidas');
      }

      console.log(`📸 Capturando: ${video.videoWidth}x${video.videoHeight}`);

      const canvas = document.createElement('canvas');
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      
      const ctx = canvas.getContext('2d');
      if (!ctx) {
        throw new Error('No se pudo obtener contexto del canvas');
      }

      ctx.drawImage(video, 0, 0);
      const imageData = canvas.toDataURL('image/jpeg', 0.8);
      
      if (imageData.length < 1000) {
        throw new Error('La imagen capturada está vacía');
      }
      
      console.log(`✅ Imagen capturada: ${imageData.length} bytes`);
      setImage(imageData);
      stopCamera();
    } catch (error: any) {
      console.error(`❌ Error al capturar: ${error.message}`);
      setErrorMessage(`Error al capturar: ${error.message}`);
    }
  }, [stopCamera]);

  const handleAccept = () => {
    if (onCapture && image) {
      onCapture(image);
    }
    setImage(null);
  };

  const handleCancel = () => {
    setImage(null);
    setHasPermission(null);
    setCameraActive(false);
    setErrorMessage('');
    setCurrentConfigIndex(0);
    setVideoReady(false);
    setVideoLoaded(false);
    setDebugInfo('');
    stopCamera();
    if (onCancel) onCancel();
  };

  const handleRetry = () => {
    setHasPermission(null);
    setErrorMessage('');
    setCurrentConfigIndex(0);
    setVideoReady(false);
    setVideoLoaded(false);
    setDebugInfo('');
    startCamera();
  };

  const handleHelpClose = () => {
    setShowHelp(false);
    if (helpChecked) {
      localStorage.setItem(HELP_KEY, '1');
    }
  };

  const handleBack = () => {
    handleCancel();
  };

  const base64ToFile = (base64: string, filename: string): File => {
    const arr = base64.split(',');
    const match = arr[0].match(/:(.*?);/);
    const mime = match ? match[1] : 'image/jpeg';
    const bstr = atob(arr[1]);
    let n = bstr.length;
    const u8arr = new Uint8Array(n);
    while (n--) {
      u8arr[n] = bstr.charCodeAt(n);
    }
    return new File([u8arr], filename, { type: mime });
  };

  const analyzeImage = async () => {
    if (image) {
      try {
        setIsAnalyzing(true);
        setAnalysisResult(null);
        const file = base64ToFile(image, 'captura.jpg');
        const result = await analyzeFoodImage(file);
        setAnalysisResult(JSON.stringify(result, null, 2));
      } catch (error) {
        console.error('Error al analizar la imagen:', error);
        setAnalysisResult('Error al analizar la imagen. Por favor, intenta de nuevo.');
      } finally {
        setIsAnalyzing(false);
      }
    }
  };

  return (
    <Box sx={{ position: 'fixed', inset: 0, bgcolor: 'black', zIndex: 1300, display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center' }}>
      {/* Modal de ayuda */}
      <Modal open={showHelp} onClose={handleHelpClose} sx={{ zIndex: 2000 }}>
        <Box sx={{
          bgcolor: '#1a1a1a', 
          color: 'white', 
          borderRadius: 3,
          p: 3, 
          maxWidth: 380, 
          mx: 'auto', 
          mt: 6,
          border: 'none',
          outline: 'none',
          zIndex: 2100
        }}>
          <Typography 
            variant="h6" 
            fontWeight={700} 
            mb={3} 
            align="center"
            sx={{ fontSize: '18px', lineHeight: 1.3 }}
          >
            Cómo obtener un<br />resultado más preciso
          </Typography>
          <Stack direction="row" spacing={2} justifyContent="center" mb={2}>
            <Box sx={{ position: 'relative' }}>
              <img 
                src={ayudaImgOk} 
                alt="Correcto" 
                style={{ 
                  width: 140, 
                  height: 120,
                  borderRadius: 12, 
                  objectFit: 'cover'
                }} 
              />
              <Box sx={{ 
                position: 'absolute', 
                top: -8, 
                left: '50%', 
                transform: 'translateX(-50%)',
                bgcolor: '#4caf50',
                borderRadius: '50%',
                p: 0.5
              }}>
                <CheckCircleIcon sx={{ color: 'white', fontSize: 24 }} />
              </Box>
            </Box>
            <Box sx={{ position: 'relative' }}>
              <img 
                src={ayudaImgNo} 
                alt="Incorrecto" 
                style={{ 
                  width: 140, 
                  height: 120,
                  borderRadius: 12, 
                  objectFit: 'cover'
                }} 
              />
              <Box sx={{ 
                position: 'absolute', 
                top: -8, 
                left: '50%', 
                transform: 'translateX(-50%)',
                bgcolor: '#f44336',
                borderRadius: '50%',
                p: 0.5
              }}>
                <CancelIcon sx={{ color: 'white', fontSize: 24 }} />
              </Box>
            </Box>
          </Stack>
          <Stack direction="row" spacing={2} justifyContent="space-between" mb={4}>
            <Box sx={{ width: '48%' }}>
              <Stack direction="row" alignItems="center" spacing={1} mb={1}>
                <CheckCircleIcon sx={{ color: '#4caf50', fontSize: 18 }} />
                <Typography variant="body2" color="white" fontWeight={500}>
                  Alimento dentro del marco
                </Typography>
              </Stack>
              <Stack direction="row" alignItems="center" spacing={1}>
                <CheckCircleIcon sx={{ color: '#4caf50', fontSize: 18 }} />
                <Typography variant="body2" color="white" fontWeight={500}>
                  Todos los ingredientes visibles
                </Typography>
              </Stack>
            </Box>
            <Box sx={{ width: '48%' }}>
              <Stack direction="row" alignItems="center" spacing={1} mb={1}>
                <CancelIcon sx={{ color: '#f44336', fontSize: 18 }} />
                <Typography variant="body2" color="white" fontWeight={500}>
                  Alimento muy cerca
                </Typography>
              </Stack>
              <Stack direction="row" alignItems="center" spacing={1}>
                <CancelIcon sx={{ color: '#f44336', fontSize: 18 }} />
                <Typography variant="body2" color="white" fontWeight={500}>
                  Ingredientes no visibles
                </Typography>
              </Stack>
            </Box>
          </Stack>
          <FormControlLabel
            control={<Checkbox checked={helpChecked} onChange={e => setHelpChecked(e.target.checked)} sx={{ color: 'white' }} />}
            label={<Typography color="white">No mostrar más</Typography>}
            sx={{ mb: 2 }}
          />
          <Button
            variant="contained"
            fullWidth
            onClick={handleHelpClose} 
            sx={{ 
              fontWeight: 700,
              bgcolor: '#FFC107',
              color: '#1a1a1a',
              fontSize: '16px',
              py: 1.5,
              borderRadius: 2,
              '&:hover': {
                bgcolor: '#FFB300'
              }
            }}
          >
            Listo
          </Button>
        </Box>
      </Modal>

      {/* Pantalla de error */}
      {hasPermission === false && (
        <Box sx={{ textAlign: 'center', p: 3, maxWidth: 400 }}>
          <Typography color="error" mb={2} variant="h6">Error de Cámara</Typography>
          <Typography color="white" mb={3} variant="body2" sx={{ lineHeight: 1.5 }}>
            {errorMessage}
          </Typography>
          
          <Stack direction="row" spacing={2} justifyContent="center">
            <Button 
              variant="outlined" 
              color="secondary" 
              onClick={handleCancel}
              sx={{ fontWeight: 700 }}
            >
              Volver
            </Button>
            <Button 
              variant="contained" 
              color="primary" 
              onClick={handleRetry}
              startIcon={<RefreshIcon />}
              sx={{ fontWeight: 700 }}
            >
              Reintentar
            </Button>
          </Stack>
        </Box>
      )}
      
      {/* Spinner de carga */}
      {isLoading && (
        <Box sx={{ textAlign: 'center', p: 3 }}>
          <CircularProgress size={60} sx={{ color: 'primary.main', mb: 2 }} />
          <Typography color="white" mb={1} variant="h6">Iniciando cámara...</Typography>
          <Typography color="white" variant="body2" sx={{ opacity: 0.8 }}>
            {isMobile() ? 'Configurando cámara para móvil' : 'Configurando cámara'}
          </Typography>
        </Box>
      )}
      
      {/* Previsualización de imagen */}
      {image && (
        <Box sx={{ position: 'relative', width: '100vw', height: '100vh', maxWidth: 420, maxHeight: 700, display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', bgcolor: 'black' }}>
          <img src={image} alt="Previsualización" style={{ width: '100%', height: '100%', objectFit: 'contain', borderRadius: 16 }} />
          <Stack direction="row" spacing={2} sx={{ position: 'absolute', bottom: 32, left: '50%', transform: 'translateX(-50%)', width: '90%' }}>
            <Button variant="outlined" color="secondary" fullWidth sx={{ fontWeight: 700, fontSize: 18 }} onClick={handleCancel}>
              Cancelar
            </Button>
            <Button variant="contained" color="primary" fullWidth sx={{ fontWeight: 700, fontSize: 18 }} onClick={handleAccept}>
              Aceptar
            </Button>
          </Stack>
        </Box>
      )}
      
      {/* Pantalla inicial */}
      {hasPermission === null && !cameraActive && !image && !isLoading && (
        <Box sx={{ textAlign: 'center', p: 3 }}>
          <Typography color="white" mb={3} variant="h6">Cámara de Alimentos</Typography>
          <Typography color="white" mb={4} variant="body2">
            Toma una foto de tu comida para analizar su contenido nutricional
          </Typography>
          <Button
            variant="contained"
            color="primary"
            onClick={startCamera}
            sx={{
              fontSize: '1.2rem',
              fontWeight: 700,
              py: 2,
              px: 4,
              borderRadius: 3,
              boxShadow: 3
            }}
          >
            Activar Cámara
          </Button>
          {!isSecureContext() && (
            <Typography color="warning.main" mt={2} variant="body2">
              ⚠️ Se requiere HTTPS para usar la cámara
            </Typography>
          )}
        </Box>
      )}
      
      {/* Vista de cámara activa */}
      {hasPermission && cameraActive && !image && !isLoading && (
        <Box sx={{ 
          position: 'relative', 
          width: '100vw', 
          height: '100vh', 
          maxWidth: 420, 
          maxHeight: 700, 
          display: 'flex', 
          flexDirection: 'column', 
          justifyContent: 'center', 
          alignItems: 'center', 
          overflow: 'hidden'
        }}>
          {/* Botones superiores */}
          <IconButton 
            onClick={handleBack} 
            sx={{ 
              position: 'absolute', 
              top: 16, 
              left: 16, 
              color: 'white', 
              bgcolor: 'rgba(0,0,0,0.8)', 
              zIndex: 10 
            }}
          >
            <ArrowBackIosNewIcon />
          </IconButton>
          
          <IconButton 
            onClick={() => setShowHelp(true)} 
            sx={{ 
              position: 'absolute', 
              top: 16, 
              right: 16, 
              color: 'white', 
              bgcolor: 'rgba(0,0,0,0.8)', 
              zIndex: 10 
            }}
          >
            <InfoOutlinedIcon />
          </IconButton>
          
          {/* Indicador de video no listo */}
          {!videoReady && (
            <Box sx={{ 
              position: 'absolute', 
              top: '50%', 
              left: '50%', 
              transform: 'translate(-50%, -50%)',
              zIndex: 20,
              textAlign: 'center',
              bgcolor: 'rgba(0,0,0,0.8)',
              borderRadius: 2,
              p: 3
            }}>
              <CircularProgress size={40} sx={{ color: 'white', mb: 2 }} />
              <Typography color="white" variant="body2" mb={2}>
                {videoLoaded ? 'Video cargado - configurando reproducción' : 'Cargando video...'}
              </Typography>
              {debugInfo && (
                <Typography color="orange" variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                  {debugInfo}
                </Typography>
              )}
            </Box>
          )}
          
          {/* Video element */}
          <video
            ref={videoRef}
            muted
            playsInline
            autoPlay
            controls={false}
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              backgroundColor: '#000',
              borderRadius: 16,
              position: 'relative',
              zIndex: 1
            }}
          />
          
          {/* Marco del viewfinder */}
          <Box sx={{ 
            position: 'absolute', 
            top: '20%', 
            left: '8%', 
            width: '84%', 
            height: '45%', 
            border: '4px solid white', 
            borderRadius: 6, 
            pointerEvents: 'none', 
            zIndex: 5,
            boxShadow: '0 0 20px rgba(255,255,255,0.5)'
          }} />
          
          {/* Texto instruccional */}
          <Typography sx={{ 
            position: 'absolute', 
            bottom: videoReady ? 120 : 250, 
            left: 16, 
            right: 16,
            color: 'white', 
            textAlign: 'center', 
            fontSize: 18, 
            fontWeight: 600, 
            textShadow: '0 2px 8px rgba(0,0,0,0.8)', 
            zIndex: 5,
            padding: '8px 16px',
            bgcolor: 'rgba(0,0,0,0.7)',
            borderRadius: 2
          }}>
            {videoReady ? '📸 Listo para tomar foto' : '⏳ Preparando video...'}
          </Typography>
          
          {/* Botón de captura */}
          <IconButton
            onClick={captureImage}
            disabled={!videoReady}
            sx={{
              position: 'absolute',
              bottom: 48,
              left: '50%',
              transform: 'translateX(-50%)',
              bgcolor: videoReady ? 'white' : 'rgba(255,255,255,0.3)',
              width: 70,
              height: 70,
              border: '4px solid #FFD600',
              boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
              zIndex: 10,
              transition: 'transform 0.1s ease-in-out',
              '&:hover': {
                boxShadow: videoReady ? '0 6px 16px rgba(0,0,0,0.4)' : '0 4px 12px rgba(0,0,0,0.3)',
              },
              '&:active': {
                transform: videoReady ? 'translateX(-50%) scale(0.95)' : 'translateX(-50%)',
              },
              '&:disabled': {
                opacity: 0.4
              }
            }}
          >
            <Box sx={{ 
              width: 44, 
              height: 44, 
              bgcolor: videoReady ? '#fffde7' : 'rgba(255,253,231,0.3)', 
              borderRadius: '50%'
            }} />
          </IconButton>
        </Box>
      )}

      {/* Resultado del análisis */}
      {analysisResult && (
        <Box sx={{ mt: 2, p: 2, bgcolor: 'background.paper', borderRadius: 1 }}>
          <Typography variant="h6" gutterBottom>
            Resultado del Análisis:
          </Typography>
          <Typography variant="body1" component="pre" sx={{ whiteSpace: 'pre-wrap' }}>
            {analysisResult}
          </Typography>
        </Box>
      )}
    </Box>
  );
}; 